import { Paragraph } from "@albertine/design-system/components/Paragraph";
import { ShimmerLoading } from "@albertine/design-system/components/ShimmerLoading";
import { parseRequestExamplesParentControlMessageData } from "@albertine/types";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useAlbertineTranslation } from "albertine-shared-web";
import { isEmbeddedInParentFrame } from "../../lib/parentFrame";
import { StorySlideshowParentVisibilityProvider } from "../-components/story-slideshow/StorySlideshowParentVisibilityProvider";
import type { StorySlideshowParentControl } from "../-components/story-slideshow/storySlideshowTypes";
import { useStorySlideshowDocumentLock } from "../-components/story-slideshow/useStorySlideshowDocumentLock";
import { requestExamplesSearchSchema } from "./-components/RequestExamplesVariant";
import { RequestExamplesWipeSwipeView } from "./-components/RequestExamplesWipeSwipeView";
import { useRequestExamplesRoute } from "./useRequestExamplesRoute";

export const Route = createFileRoute("/request-examples/")({
    validateSearch: requestExamplesSearchSchema,
    component: RouteComponent,

    head: ({ match }) => {
        const { tenant, locale } = match.search;
        return {
            meta: [
                {
                    title: `Request examples - ${tenant} ${locale}`,
                },
            ],
        };
    },

    beforeLoad: () => {
        document.documentElement.style.setProperty("--color-body-top", "transparent");
        document.documentElement.style.setProperty(
            "--color-body-bottom",
            "transparent",
        );
    },

    onError: () => {
        throw notFound();
    },
});

function RouteComponent() {
    const t = useAlbertineTranslation();
    const { variant } = Route.useSearch();
    const {
        content,
        handleClose,
        handleCta,
        handleSheetDismissDrag,
        isError,
        isPending,
    } = useRequestExamplesRoute();
    useStorySlideshowDocumentLock();

    if (isPending) {
        if (isEmbeddedInParentFrame()) {
            return null;
        }

        return (
            <div
                className="h-dvh w-full"
                role="status"
                aria-live="polite"
                aria-label={t("request_examples.loading")}
            >
                <ShimmerLoading className="size-full" />
            </div>
        );
    }

    if (isError || !content) {
        return <RequestExamplesState message={t("request_examples.unavailable")} />;
    }

    return (
        <StorySlideshowParentVisibilityProvider
            parseParentControl={parseRequestExamplesParentControl}
        >
            <RequestExamplesWipeSwipeView
                slides={content}
                variant={variant}
                onClose={handleClose}
                onCta={handleCta}
                onSheetDismissDrag={handleSheetDismissDrag}
            />
        </StorySlideshowParentVisibilityProvider>
    );
}

function parseRequestExamplesParentControl(
    data: unknown,
): StorySlideshowParentControl | null {
    const message = parseRequestExamplesParentControlMessageData(data);

    if (!message) {
        return null;
    }

    switch (message.type) {
        case "requestExamples.visibility":
            return { type: "visibility", visible: message.visible };
        case "requestExamples.reset":
            return { type: "reset" };
        default: {
            const unhandledMessage: never = message;
            return unhandledMessage;
        }
    }
}

type RequestExamplesStateProps = {
    message: string;
};

function RequestExamplesState({ message }: RequestExamplesStateProps) {
    return (
        <div className="grid h-full place-items-center bg-surface-inverse px-24 text-center">
            <Paragraph color="inverse-primary">{message}</Paragraph>
        </div>
    );
}
